import React, {useEffect, useMemo, useState} from "react";
import {
    ActionRow,
    ActionRowButton,
    ActionRowDropdownButton,
    DropdownItem,
    DropdownItemGroup,
    DropdownItemGroups,
    Icon,
    TopBar,
    useTheme
} from "peggirkit";
import {ListBulletIcon, MapPinIcon, PlusIcon} from "@heroicons/react/20/solid";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {RestaurantLocation} from "@/data/entities/restaurant";
import {CheckIcon, MoonIcon, SunIcon} from "@heroicons/react/16/solid";
import {FormattedMessage, FormattedTime} from "react-intl";
import {usePos} from "@/context/pos/PosProvider";
import {ClockIcon} from "@heroicons/react/24/outline";
import {posEditOrder, posOrders} from "@/routing/Router";
import {useNavigate} from "react-router-dom";
import {SetStateFn} from "@/utils/SetStateUtils";


const msUntilNextMinute = (): number => 60_000 - (Date.now() % 60_000);

type Props = {
    setShowTimeRegistration: SetStateFn<boolean>,
};

const PosTopBar = ({setShowTimeRegistration}: Props) => {
    const navigate = useNavigate();
    const {locations, dashboardLocation, setDashboardLocation} = useRestaurant();
    const {activeOrderCount, isRefreshing} = usePos();
    const [now, setNow] = useState<Date>(new Date());
    const {isDark, toggleTheme} = useTheme();
    const ThemeToggleIcon = useMemo(() => isDark ? SunIcon : MoonIcon, [isDark]);

    useEffect(() => {
        let timeout: number;

        // Schedule each tick on the next minute boundary, so the clock
        // doesn't drift and switches exactly on hh:mm:00.
        const tick = () => {
            setNow(new Date());
            timeout = window.setTimeout(tick, msUntilNextMinute());
        };

        timeout = window.setTimeout(tick, msUntilNextMinute());
        return () => window.clearTimeout(timeout);
    }, []);

    return (
        <TopBar
            containerClassname={"z-[50]"}
            startActionsStylingType={"button"}
            startActions={[
                <ActionRowDropdownButton
                    key={"location"}
                    iconBefore={<Icon icon={MapPinIcon}/>}
                    disabled={isRefreshing}
                    items={
                        <DropdownItemGroups>
                            <DropdownItemGroup>
                                {locations.map((l: RestaurantLocation) => {
                                    const selected = l.id === dashboardLocation.id
                                    return (
                                        <DropdownItem
                                            key={l.id}
                                            buttonAction={{
                                                as: "button",
                                                actionProps: {
                                                    onClick: () => {
                                                        setDashboardLocation(l);
                                                        navigate(posOrders().absolutePath());
                                                    },
                                                },
                                            }}
                                            disabled={selected}
                                            icon={selected ? <Icon icon={CheckIcon}/> : "empty"}
                                        >
                                            {l.name}
                                        </DropdownItem>
                                    );
                                })}
                            </DropdownItemGroup>
                        </DropdownItemGroups>
                    }
                >
                    {dashboardLocation.name}
                </ActionRowDropdownButton>,
                <ActionRowButton
                    key={"orders"}
                    iconBefore={<Icon icon={ListBulletIcon}/>}
                    loading={isRefreshing}
                    buttonAction={{
                        as: "reactRouterLink",
                        actionProps: {
                            to: posOrders().absolutePath(),
                        },
                    }}
                >
                    <FormattedMessage
                        id={"n-orders"}
                        values={{n: activeOrderCount}}
                    />
                </ActionRowButton>,
                <ActionRowButton
                    key={"new-order"}
                    selected={true}
                    disabled={isRefreshing}
                    iconBefore={<Icon icon={PlusIcon}/>}
                    buttonAction={{
                        as: "reactRouterLink",
                        actionProps: {
                            to: posEditOrder().absolutePath(),
                        },
                    }}
                >
                    <FormattedMessage id={"new-order"}/>
                </ActionRowButton>,
            ]}
            endActions={
                <ActionRow>
                    <ActionRowButton
                        variant={"solid"}
                        color={"secondary"}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: e => {
                                    e.preventDefault();
                                    setShowTimeRegistration(true);
                                }
                            },
                        }}
                    >
                        <FormattedMessage id={"clockIn"}/>
                    </ActionRowButton>
                    <ActionRowButton
                        buttonAction={{
                            as: "button",
                            actionProps: {onClick: toggleTheme},
                        }}
                        variant={"text"}
                        iconBefore={<Icon icon={ThemeToggleIcon}/>}
                        borderRadius={"rounded-full"}
                        iconOnly
                    >
                        <FormattedMessage id={isDark ? "lightMode" : "darkMode"}/>
                    </ActionRowButton>
                    <ActionRowButton
                        buttonAction={{as: "button"}}
                        variant={"text"}
                        iconBefore={<Icon icon={ClockIcon}/>}
                        disabled
                    >
                        <FormattedTime
                            value={now}
                            hour={"2-digit"}
                            minute={"2-digit"}
                            hourCycle={"h23"}
                        />
                    </ActionRowButton>
                </ActionRow>
            }
        />
    );
};

export default PosTopBar;